import React from 'react';
import { Calendar, Clock, Globe, Lock } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SchedulingUpgradeCard, SchedulingDiscoveryCard } from '../ui/SchedulingUpgradeCard';
import { useSchedulingFeature } from '../../context/SchedulingFeatureContext';
import { useNavigate } from 'react-router-dom';

// Common timezones for selection
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
];

export const SchedulingForm = ({ 
  scheduleState, 
  setScheduleState, 
  uploading,
  onClose 
}) => {
  const navigate = useNavigate();
  const { 
    hasSchedulingAccess, 
    shouldShowDiscovery,
    trackUpgradeClicked 
  } = useSchedulingFeature();

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const updateSchedule = (field, value) => {
    setScheduleState(prev => ({ ...prev, [field]: value }));
  };

  if (!hasSchedulingAccess) {
    return (
      <div className="space-y-4 border-t border-gray-700 pt-4" role="region" aria-label="Post scheduling locked">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-gray-500" aria-hidden="true" />
            <Label className="text-gray-400 font-semibold">Schedule Post</Label>
          </div>
          <Switch
            checked={false}
            disabled={true}
            aria-label="Post scheduling is locked"
          />
        </div>
        <SchedulingUpgradeCard 
          variant="inline" 
          placement="upload_modal"
          onUpgradeClick={() => {
            trackUpgradeClicked('upload_modal');
            onClose?.();
            navigate('/pricing?highlight=on_ya_royalties&feature=scheduling');
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 border-t border-gray-700 pt-4">
      {/* Discovery tip for users who have access but haven't used it */}
      {shouldShowDiscovery && (
        <SchedulingDiscoveryCard 
          variant="tooltip" 
          placement="upload_modal"
          className="mb-3"
        />
      )}
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-yellow-400" aria-hidden="true" />
          <Label className="text-white font-semibold" id="schedule-label">Schedule Post</Label>
        </div>
        <Switch
          checked={scheduleState.enabled}
          onCheckedChange={(checked) => updateSchedule('enabled', checked)}
          disabled={uploading}
          aria-labelledby="schedule-label"
          aria-describedby="schedule-description"
          data-testid="schedule-toggle"
        />
      </div>
      <p id="schedule-description" className="sr-only">
        Toggle to schedule this post for a future date and time
      </p>

      {scheduleState.enabled && (
        <div className="space-y-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700" role="region" aria-label="Scheduling options">
          <p className="text-sm text-gray-400">
            Schedule your content to be published at a specific date and time.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="schedule-date" className="text-gray-300 flex items-center gap-2">
                <Calendar className="w-4 h-4" aria-hidden="true" />
                Date
              </Label>
              <Input
                id="schedule-date"
                type="date"
                value={scheduleState.date}
                onChange={(e) => updateSchedule('date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="bg-gray-800 border-gray-600 text-white min-h-[44px]"
                disabled={uploading}
                aria-required="true"
                data-testid="schedule-date-input"
              />
            </div>
            
            {/* Time */}
            <div className="space-y-2">
              <Label htmlFor="schedule-time" className="text-gray-300 flex items-center gap-2">
                <Clock className="w-4 h-4" aria-hidden="true" />
                Time
              </Label>
              <Input
                id="schedule-time"
                type="time"
                value={scheduleState.time}
                onChange={(e) => updateSchedule('time', e.target.value)}
                className="bg-gray-800 border-gray-600 text-white min-h-[44px]"
                disabled={uploading}
                aria-required="true"
                data-testid="schedule-time-input"
              />
            </div>
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor="schedule-timezone" className="text-gray-300 flex items-center gap-2">
              <Globe className="w-4 h-4" aria-hidden="true" />
              Timezone
            </Label>
            <Select
              value={scheduleState.timezone}
              onValueChange={(value) => updateSchedule('timezone', value)}
              disabled={uploading}
            >
              <SelectTrigger 
                id="schedule-timezone" 
                className="bg-gray-800 border-gray-600 text-white min-h-[44px]"
                data-testid="schedule-timezone-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                <SelectItem value={userTimezone} className="text-yellow-400 font-medium min-h-[44px]">
                  Your Local Time ({userTimezone.split('/').pop().replace('_', ' ')})
                </SelectItem>
                {TIMEZONES.filter(tz => tz.value !== userTimezone).map((tz) => (
                  <SelectItem key={tz.value} value={tz.value} className="text-white min-h-[44px]">
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Preview */}
          {scheduleState.date && scheduleState.time && (
            <div 
              className="p-3 bg-gradient-to-r from-yellow-400/10 to-purple-500/10 rounded-lg border border-yellow-400/30"
              role="status"
              aria-live="polite"
              data-testid="schedule-preview"
            >
              <p className="text-sm text-gray-300">
                <span className="text-yellow-400 font-medium">Scheduled for:</span>{' '}
                {new Date(`${scheduleState.date}T${scheduleState.time}`).toLocaleString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZoneName: 'short'
                })}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SchedulingForm;
